import React, { FC } from 'react';

import { FormattedMessage } from 'react-intl';
import { useTheme } from 'styled-components';

import { statFormatter } from '@nhl/formatter-utilities';
import { BaseStyleEnums, IdEnums } from '@nhl/nhl-breakaway-ui';
import Headshot from '@nhl/nhl-react-ui/lib/Headshot';
import headshotTypes from '@nhl/nhl-react-ui/lib/Headshot/headshotTypes';

import CommonMessages from '~/core/commonMessages';
import { PlayerStatAbbrev, PlayerStatNames } from '~/core/enums';
import { useIsWebview } from '~/core/hooks/useIsWebview';
import { useSizeWithThrottle } from '~/core/hooks/useSizeWithThrottle';
import { Team } from '~/core/types';
import { getPlayerHref, parseValueFromLocalizedAttribute } from '~/core/utils';

import { GoalieLeader } from '../../../../types';
import StatDisplay from '../StatDisplay/StatDisplay';

import {
    FirstName,
    GoaltenderContainer,
    GoaltenderIdentifiers,
    GoaltenderStatsContainer,
    LastName,
    SweaterNumber,
} from './styles';

type GoaltenderDetailsProps = {
    goalie: GoalieLeader;
    team: Team;
};

type GoaltenderStatProps = GoaltenderDetailsProps & {
    gameType: IdEnums.GameType;
};

const GoaltenderDetails: FC<GoaltenderDetailsProps> = ({ goalie, team }) => {
    const theme = useTheme();
    const isWebview = useIsWebview();

    return (
        <GoaltenderContainer>
            <a href={getPlayerHref(goalie.playerId, isWebview)} rel="noreferrer">
                <Headshot
                    type={headshotTypes.GOALIE}
                    urlOverride={goalie.headshot}
                    size={64}
                    backgroundColor={theme.Team[team.abbrev]?.gamecenter}
                />
            </a>
            <GoaltenderIdentifiers href={getPlayerHref(goalie.playerId, isWebview)} rel="noreferrer">
                <FirstName>{parseValueFromLocalizedAttribute(goalie.firstName)}</FirstName>
                <LastName>{parseValueFromLocalizedAttribute(goalie.lastName)}</LastName>
                {goalie.sweaterNumber && (
                    <SweaterNumber>
                        <FormattedMessage
                            {...CommonMessages.playerInfoDescriptions.long.number}
                            values={{ sweaterNumber: goalie.sweaterNumber }}
                        />
                    </SweaterNumber>
                )}
            </GoaltenderIdentifiers>
        </GoaltenderContainer>
    );
};

const GoaltenderStats: FC<GoaltenderStatProps> = ({ gameType, goalie, team }) => {
    const screenSize = useSizeWithThrottle();

    const isMedium = screenSize.width >= BaseStyleEnums.ScreenWidthBreakpoints.medium;

    const stats = (
        <>
            <StatDisplay
                messageAbbrev={
                    gameType === IdEnums.GameType.playoffs ? PlayerStatAbbrev.playoffRecord : PlayerStatAbbrev.record
                }
                messageName={PlayerStatNames.record}
                value={goalie.record}
            />
            <StatDisplay
                messageAbbrev={PlayerStatAbbrev.goalsAgainstAvg}
                messageName={PlayerStatNames.goalsAgainstAvg}
                value={statFormatter.displayValueAs('goalsAgainstAverage')(goalie.gaa, goalie.gamesPlayed >= 1)}
            />
            <StatDisplay
                messageAbbrev={PlayerStatAbbrev.savePctg}
                messageName={PlayerStatNames.savePctg}
                value={statFormatter.displayValueAs('savePercentage')(goalie.savePctg)}
            />
            <StatDisplay
                messageAbbrev={PlayerStatAbbrev.shutouts}
                messageName={PlayerStatNames.shutouts}
                value={goalie.shutouts}
            />
        </>
    );

    return isMedium ? (
        <GoaltenderStatsContainer>
            <GoaltenderDetails goalie={goalie} team={team} />
            {stats}
        </GoaltenderStatsContainer>
    ) : (
        <div>
            <GoaltenderStatsContainer>
                <GoaltenderDetails goalie={goalie} team={team} />
            </GoaltenderStatsContainer>
            <GoaltenderStatsContainer $isStatsOnly>{stats}</GoaltenderStatsContainer>
        </div>
    );
};

export default GoaltenderStats;
